import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getData, OAuthError, PaidFeatureError, postData } from "../../backend";
import { ONBOARDING_SAMPLE_COMPANY, OnboardingDataSelection, OnboardingTemplate } from "./constants";

type GenerationStatus = "idle" | "running" | "success" | "error" | "skipped";

const REQUEST_TIMEOUT_MS = 20000;
const SAMPLE_PREVIEW_MS = 5000;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_FAILURES = 5;
const IN_PROGRESS_STATUSES = ["PENDING", "RUNNING", "PENDING_RESTART"];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), REQUEST_TIMEOUT_MS));
  return Promise.race([promise, timeout]);
}

function selectionKey(template: OnboardingTemplate, data: OnboardingDataSelection) {
  return `${template.key}:${data.kind === "hubspot_deal" ? data.dealId : "sample"}`;
}

function errorMessage(e: any, t: (key: string) => string): string {
  if (e instanceof OAuthError)
    return t("An account connection needs to be refreshed before we can generate. Reconnect it, then retry.");
  if (e instanceof PaidFeatureError)
    return t("Your plan's document limit has been reached.");
  if (typeof e?.payload?.detail === "string")
    return e.payload.detail;
  // Server 500s arrive as an HTML page in the message; never show those.
  const message = e?.message;
  if (typeof message === "string" && message.trim() && message !== "Failed to fetch" && !message.trimStart().startsWith("<") && message.length < 300)
    return message;
  return t("Generation failed. Please try again.");
}

async function waitForAutomation(automationId: string, isCurrent: () => boolean): Promise<string | null> {
  let failures = 0;
  while (isCurrent()) {
    await sleep(POLL_INTERVAL_MS);
    try {
      const automation = await getData(`/workflows/automations/${automationId}/`);
      failures = 0;
      if (!IN_PROGRESS_STATUSES.includes(automation.status))
        return automation.status;
    } catch (e) {
      failures += 1;
      // One blip shouldn't fail a run that's still going.
      if (failures >= MAX_POLL_FAILURES)
        throw e;
    }
  }
  return null;
}

async function loadGeneratedPdf(workflowId: string, automationId: string) {
  const page = await getData(`/workflows/${workflowId}/items/?page=1`);
  const items: any[] = page?.results ?? [];
  const item = items.find((i) => i.automation === automationId) ?? items[0];
  const output = (item?.outputs ?? []).find((o: any) => o.previewUrl);
  return {
    previewUrl: (output?.previewUrl as string | undefined) ?? null,
    documentName: (output?.filePdf?.name ?? output?.outputName) as string | undefined ?? null,
  };
}

export default function useOnboardingGeneration() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<GenerationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string | null>(null);

  // Bumped by every start, skip and unmount so a superseded run stops updating state.
  const runId = useRef(0);
  const lastSelection = useRef<{ template: OnboardingTemplate; data: OnboardingDataSelection } | null>(null);

  useEffect(() => {
    const run = runId;
    return () => { run.current += 1; };
  }, []);

  const start = useCallback(async (template: OnboardingTemplate, data: OnboardingDataSelection) => {
    const run = ++runId.current;
    const isCurrent = () => runId.current === run;
    lastSelection.current = { template, data };

    setStatus("running");
    setError(null);
    setWorkflowId(null);
    setPreviewUrl(null);
    setDocumentName(null);

    const timeoutMessage = t("Generation is taking too long. Retry, or skip to continue without the document.");
    const templateSlug = data.kind === "hubspot_deal" ? template.templateSlug : template.sampleTemplateSlug;

    try {
      let workflowId: string | null = null;
      try {
        const workflow = await withTimeout(postData(`/templates/${templateSlug}/duplicate/`), timeoutMessage);
        if (!isCurrent())
          return;
        workflowId = workflow.id;
        setWorkflowId(workflow.id);
      } catch (e) {
        if (data.kind !== "sample")
          throw e;
        console.error("onboarding sample template copy failed", e);
      }

      if (data.kind === "sample") {
        await sleep(SAMPLE_PREVIEW_MS);
        if (!isCurrent())
          return;
        setPreviewUrl(template.samplePdf);
        setDocumentName(`${template.label}-${ONBOARDING_SAMPLE_COMPANY.replace(/\s+/g, "-")}`);
        setStatus("success");
        return;
      }

      const options = { mode: "SOURCE_ITEM_IDS", sourceItemIds: [data.dealId], concurrent: true };
      if (!workflowId)
        throw new Error(t("Generation failed. Please try again."));
      const automation = await withTimeout(postData(`/workflows/${workflowId}/run/`, { options }), timeoutMessage);

      const finalStatus = await waitForAutomation(automation.id, isCurrent);
      if (!isCurrent())
        return;
      if (finalStatus === "CANCELLED")
        throw new Error(t("Generation was cancelled. Please try again."));
      if (finalStatus !== "COMPLETED")
        throw new Error(t("Generation failed. Please try again."));

      const pdf = await loadGeneratedPdf(workflowId, automation.id).catch(() => null);
      if (!isCurrent())
        return;
      setPreviewUrl(pdf?.previewUrl ?? null);
      setDocumentName(pdf?.documentName ?? null);
      setStatus("success");
    } catch (e) {
      if (!isCurrent())
        return;
      const message = errorMessage(e, t);
      console.error("onboarding generate failed", e);
      setStatus("error");
      setError(message);
    }
  }, [t]);

  const retry = useCallback(() => {
    if (lastSelection.current)
      start(lastSelection.current.template, lastSelection.current.data);
  }, [start]);

  const skip = useCallback(() => {
    runId.current += 1;
    setStatus("skipped");
    setPreviewUrl(null);
  }, []);

  const isFor = useCallback((template: OnboardingTemplate, data: OnboardingDataSelection) => {
    const last = lastSelection.current;
    return !!last && selectionKey(last.template, last.data) === selectionKey(template, data);
  }, []);

  return { status, error, workflowId, previewUrl, documentName, start, retry, skip, isFor };
}
